import React, { useState } from "react";
import ButtonBase from "../buttons/ButtonBase";
import TextareaBase from "./TextareaBase";
import InputXs from "./InputXs";
import { useForm, FormProvider } from "react-hook-form";
import CheckboxGroup from "./CheckboxGroup";
import { useNotifications } from "../../../../store/NotificationContext";
import { $host } from "../../../../http";
import { TextEditor } from "./TextEditor";

const AddHomeworkForm = ({ closeForm, hwId, data = null, idx = null, updateQuestions }) => {
    const [autocheckEnabled, setAutocheckEnabled] = useState(false);
    const [questionPayload, setQuestionPayload] = useState(
        data?.question
            ? { content: data.question.title, imgFile: null }
            : null
    );

    const [teacherCommentPayload, setTeacherCommentPayload] = useState(
        data?.question?.teacher_comment
            ? { content: data.question.teacher_comment }
            : null
    );


    const { showNotification } = useNotifications();

    const methods = useForm({
        defaultValues: data ? { ...data.question } : {}

    });

    const questionTypeTabs = [
        { title: "Текстовое задание", alias: "text" },
        { title: "Тест с вариантами ответа", alias: "multiple_choice" },
        { title: "Тест", alias: "test" },
    ];

    const [questionType, setQuestionType] = useState(data ? data.question.type : questionTypeTabs[0].alias);

    const createOrUpdateQuestion = async (fData) => {
        try {
            const additionalPayload = {
                min_points: 1,
                max_points: 3,
                points_criteria: fData.points_criteria || "",
                photo: questionPayload && questionPayload.imgFile ? questionPayload.imgFile : undefined,
                points: questionType !== "text" ? +fData.points : undefined,
                correct_answer: fData.correct_answer || "",
                teacher_comment: (questionType === "test" || questionType === "text")
                    ? (teacherCommentPayload?.content ?? "")
                    : undefined,
                options: questionType === "multiple_choice" ? fData.options.slice(0, -1) : undefined,
            };


            const preparedData = {
                ...fData,
                ...additionalPayload,
                title: questionPayload?.content ?? "",
                type: questionType,
                // остальные поля...
            };


            const body = new FormData();
            Object.keys(preparedData).forEach((key) => {
                if (key === 'options') {
                    body.append('options', JSON.stringify(preparedData[key]));
                    return;
                }
                if (preparedData[key] !== undefined) {
                    body.append(key, preparedData[key]);
                }
            });


            const url = data
                ? `/api/teachers/homeworks/${hwId}/questions/${data.question.id}`
                : `/api/teachers/homeworks/questions/${hwId}`;

            await $host.request(url, {
                method: data ? "PUT" : "POST",
                data: body,
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            await updateQuestions();
            showNotification("Вопрос успешно обновлен");
            closeForm();
        } catch (e) {
            console.error(e);
        }
    };

    const deleteQuestion = async () => {
        if (!data) return;
        try {
            await $host.delete(`/api/teachers/questions/${data.question.id}`);
            await updateQuestions();
            showNotification("Вопрос успешно удален");
            closeForm();
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <FormProvider {...methods}>
            <form className="mb-8" onSubmit={methods.handleSubmit(createOrUpdateQuestion)}>
                <div className="mb-8 text-lavender font-medium flex justify-between items-center bg-lavender-8 px-4 rounded-2xl min-h-[50px] basis-[278px]">
                    <div className="flex gap-2 grow-1">
                        <span>{data ? `Вопрос №${idx + 1}` : "Новый вопрос"}</span>
                    </div>
                    <div className="flex items-center gap-8 min-h-[20px]">
                        {(questionType === "multiple_choice" || questionType === "test") && (
                            <>
                                <span>Введите баллы:</span>
                                <div className="flex items-center justify-end gap-1">
                                    <InputXs
                                        type="number"
                                        name="points"
                                        defaultValue={1}
                                        ref={methods.register("points")}
                                        className="max-w-[2em] text-center"
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className="mt-4 mb-8">
                    <TextEditor placeholder="Введите текст задания" setPayload={setQuestionPayload} data={data?.question} />
                </div>

                <div className="flex gap-2">
                    {questionTypeTabs.map((q) => (
                        <ButtonBase
                            type="button"
                            key={q.alias}
                            onClick={() => setQuestionType(q.alias)}
                            activestate={questionType === q.alias ? "default" : "tab-inactive"}
                            className="basis-3/6 grow-1"
                        >
                            {q.title}
                        </ButtonBase>
                    ))}
                </div>

                <div className="mt-4">
                    {(questionType === "text" || questionType === "test") && (
                        <div className="flex flex-col gap-2">
                            <TextareaBase
                                rows="5"
                                name="correct_answer"
                                placeholder="Введите ответ на задание"
                                ref={methods.register("correct_answer")} // ✅ Регистрируем поле
                            />
                            {questionType === "text" && (
                                <TextareaBase
                                    rows="5"
                                    name="points_criteria"
                                    placeholder="Введите критерии оценки"
                                    ref={methods.register("points_criteria")} // ✅ Теперь поле регистрируется
                                />
                            )}
                            {(questionType === "test" || questionType === "text") && (
                                <TextEditor
                                    setPayload={setTeacherCommentPayload}
                                    rows="5"
                                    name="teacher_comment"
                                    placeholder="Введите пояснение от учителя"
                                    data={{ title: data?.question?.teacher_comment || "" }} // ✅ ПРАВИЛЬНО!
                                />

                            )}

                        </div>
                    )}
                    {questionType === "multiple_choice" && <CheckboxGroup data={data} />}
                </div>

                <div className="flex justify-between items-center mt-8">
                    <div className="flex gap-2">
                        <ButtonBase type="submit">Сохранить</ButtonBase>
                        <ButtonBase activestate="light" disabled={!autocheckEnabled}>Автопроверка</ButtonBase>
                    </div>
                    <div className="flex gap-2">
                        <ButtonBase onClick={closeForm} activestate="light">Отмена</ButtonBase>
                        {data && <ButtonBase onClick={deleteQuestion} activestate="danger">Удалить</ButtonBase>}
                    </div>
                </div>
            </form>
        </FormProvider>
    );
};

export default AddHomeworkForm;
